/**
 * CameraCapture — in-app camera voor de media-laag (fase 1 CompanyCam-exit).
 *
 * Vervangt de sprong naar de CompanyCam-app: full-screen zoeker via
 * getUserMedia (camera + microfoon), serie-modus (blijf klikken, teller loopt
 * op), en video mét geluid via MediaRecorder (max 60s / 720p; `maxSeconds`
 * verhoogt dat voor social-clips). Elke capture gaat direct als blob de
 * Dexie mediaQueue in (enqueueMedia) en wordt door de upload-worker naar R2
 * gebracht. Er landt NOOIT iets in de galerij van het toestel: geen
 * <input capture>, geen download, geen share-sheet.
 *
 * Eén uitzondering sinds 22-10: het SOCIAL-FILMPJE (fase social, alleen in de
 * wasserij, nooit bij een klant thuis). De MediaRecorder-opname bevriest op de
 * toestellen na 12 tot 20 seconden terwijl het geluid doorloopt (#93), en
 * Instagram wil 30 tot 60 seconden echte wasactie, staand en dichtbij. Daarvoor
 * opent de knop "Social-filmpje" de camera-app van het toestel via
 * <input capture>. Op iOS landt zo'n opname niet in Foto's; op Android kan dat
 * per camera-app verschillen. De clip gaat als fase 'social' onder het kleed
 * en telt in de selector als "social-knop" (voorrang).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { WbButton, WbModal } from './wb';
import {
  enqueueMedia,
  getOpslagRuimte,
  type MediaDoel,
  type MediaFase,
} from '../lib/media/queue';

const VIDEO_MAX_SECONDS = 60;
const VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: 'environment',
  width: { ideal: 1280 },
  height: { ideal: 720 },
};
const FOTO_JPEG_QUALITY = 0.85;

const FASE_LABELS: Record<MediaFase, string> = {
  voorinspectie: 'Voorinspectie',
  reinigen: 'Reinigen',
  'na-inspectie': 'Na-inspectie',
  klacht: 'Klacht',
  risico: 'Risico',
  ophaal: 'Ophaal',
  aflevering: 'Aflevering',
  referentie: 'Referentie',
  social: 'Social',
};

interface Props {
  fase: MediaFase;
  doel: MediaDoel;
  /** Optionele context-regel in de header (bv. item- of WO-nummer) */
  subtitle?: string;
  /** Status__c van het WI op het schietmoment — landt als capture_status op
   *  de media-rij (klant-timeline: nabehandeling vs. eerste reiniging) */
  captureStatus?: string;
  /** Harde cap op video-opname in seconden (default 60; social-clips 180) */
  maxSeconds?: number;
  onClose: (aantalGemaakt: number) => void;
}

export function CameraCapture({
  fase,
  doel,
  subtitle,
  captureStatus,
  maxSeconds = VIDEO_MAX_SECONDS,
  onClose,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const socialInputRef = useRef<HTMLInputElement>(null);
  const countRef = useRef(0);

  const [ready, setReady] = useState(false);
  const [denied, setDenied] = useState(false);
  const [count, setCount] = useState(0);
  const [thumbs, setThumbs] = useState<string[]>([]);
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [flash, setFlash] = useState(false);
  /** Opslag nam de laatste capture niet aan. Blokkeert de sluiter: doorklikken
   *  zou de wasser laten denken dat het kleed vastgelegd is. */
  const [opslagFout, setOpslagFout] = useState<string | null>(null);

  // Camera starten/stoppen
  useEffect(() => {
    let cancelled = false;

    async function start() {
      try {
        // Mét microfoon: video's dragen het echte wasserij-geluid (water, schuim,
        // borstel), en dat is de audio van de social-Reels (besluit 02-09).
        // Wordt de microfoon geweigerd, dan gewoon zonder geluid doorgaan: een
        // stille clip is beter dan geen clip. De zoeker zelf blijft muted.
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: VIDEO_CONSTRAINTS,
            audio: true,
          });
        } catch (audioErr) {
          console.warn('[CameraCapture] microfoon niet beschikbaar, zonder geluid:', audioErr);
          stream = await navigator.mediaDevices.getUserMedia({
            video: VIDEO_CONSTRAINTS,
            audio: false,
          });
        }
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        setReady(true);
      } catch (err) {
        console.warn('[CameraCapture] getUserMedia faalde:', err);
        if (!cancelled) setDenied(true);
      }
    }

    void start();

    return () => {
      cancelled = true;
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
      if (recordTimerRef.current) clearTimeout(recordTimerRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      // Object-URLs van thumbnails opruimen gebeurt bij unmount door de browser
      // zodra de pagina weg is; expliciet revoken bij sluiten doen we in handleClose.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const registreerCapture = useCallback(
    async (blob: Blob, mimeType: string, type: 'foto' | 'video', faseOverride?: MediaFase) => {
      // De blob in IndexedDB is de enige kopie: geen galerij, en op de server
      // staat pas iets zodra de upload lukt. Faalt de opslag (quota vol,
      // IndexedDB dicht), dan is de foto weg op het moment van klikken. Dat
      // mag nooit stil gebeuren, dus vangen en de sluiter dichtzetten.
      try {
        await enqueueMedia({ blob, mimeType, type, fase: faseOverride ?? fase, doel, captureStatus });
      } catch (err) {
        const ruimte = await getOpslagRuimte();
        const vol =
          (err instanceof Error && err.name === 'QuotaExceededError') ||
          (ruimte?.aandeel ?? 0) > 0.9;
        console.error('[CameraCapture] opslag weigerde de capture:', err, ruimte);
        setOpslagFout(
          vol
            ? 'Het toestel heeft geen ruimte meer. Deze foto is NIET opgeslagen. Zorg dat de wachtende uploads eerst wegkomen (wifi, app open) en probeer daarna opnieuw.'
            : 'Deze foto is NIET opgeslagen op het toestel. Sluit de camera, controleer de wachtrij in de header en meld dit.'
        );
        return;
      }

      setOpslagFout(null);
      countRef.current += 1;
      setCount(countRef.current);
      if (type === 'foto') {
        setThumbs((prev) => [...prev.slice(-4), URL.createObjectURL(blob)]);
      }
      if (navigator.vibrate) navigator.vibrate(50);
    },
    [fase, doel, captureStatus]
  );

  // Social-filmpje: de camera-app van het toestel (zie kop). Landt als fase 'social'.
  const socialFilmpjeMogelijk = fase === 'reinigen' || fase === 'social';
  const onSocialFilmpje = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      // iOS geeft video/quicktime (.mov); een leeg type is in de praktijk ook mp4/mov.
      await registreerCapture(file, file.type || 'video/mp4', 'video', 'social');
    },
    [registreerCapture]
  );

  // Foto: frame van de zoeker naar JPEG-blob via canvas
  const maakFoto = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !ready || recording) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);

    setFlash(true);
    setTimeout(() => setFlash(false), 120);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', FOTO_JPEG_QUALITY)
    );
    if (!blob) return;
    await registreerCapture(blob, 'image/jpeg', 'foto');
  }, [ready, recording, registreerCapture]);

  // Video: MediaRecorder op de bestaande stream, max `maxSeconds`
  const stopVideo = useCallback(() => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
    if (recordTimerRef.current) {
      clearTimeout(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    setRecording(false);
    setRecordSeconds(0);
  }, []);

  const startVideo = useCallback(() => {
    const stream = streamRef.current;
    if (!stream || recording) return;

    const mimeType = MediaRecorder.isTypeSupported('video/mp4')
      ? 'video/mp4'
      : 'video/webm';
    const recorder = new MediaRecorder(stream, { mimeType });
    const chunks: Blob[] = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mimeType });
      if (blob.size > 0) {
        void registreerCapture(blob, mimeType, 'video');
      }
    };

    recorderRef.current = recorder;
    recorder.start();
    setRecording(true);
    setRecordSeconds(0);

    // Teller + harde cap op maxSeconds
    const tick = (seconde: number) => {
      recordTimerRef.current = setTimeout(() => {
        setRecordSeconds(seconde);
        if (seconde >= maxSeconds) {
          stopVideo();
        } else {
          tick(seconde + 1);
        }
      }, 1000);
    };
    tick(1);
  }, [recording, registreerCapture, stopVideo, maxSeconds]);

  const handleClose = useCallback(() => {
    if (recording) stopVideo();
    thumbs.forEach((url) => URL.revokeObjectURL(url));
    onClose(countRef.current);
  }, [recording, stopVideo, thumbs, onClose]);

  // Permissie geweigerd of camera niet beschikbaar
  if (denied) {
    return (
      <WbModal
        open
        onClose={handleClose}
        titel="Geen camera-toegang"
        acties={<WbButton size="lg" onClick={handleClose}>Sluiten</WbButton>}
      >
        <p className="text-base text-wb-inkt2">
          Zet camera-toegang voor deze app aan in de instellingen van je toestel (browser-permissies) en probeer het opnieuw.
        </p>
      </WbModal>
    );
  }

  return (
    <div className="fixed inset-0 z-wb-lightbox bg-black">
      {/* Zoeker — vult het hele scherm; bediening zweeft eroverheen */}
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        className="absolute inset-0 w-full h-full object-cover"
      />
      {flash && <div className="absolute inset-0 bg-white/80" />}
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center font-mono text-white/80 text-sm">
          Camera starten…
        </div>
      )}

      {/* Top-overlay: context + Klaar */}
      <div
        className="absolute top-0 inset-x-0 flex items-center justify-between gap-2 px-4 pb-6 bg-gradient-to-b from-black/70 to-transparent"
        style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}
      >
        <div className="min-w-0">
          <p className="font-wb-heading font-bold text-base uppercase tracking-wide text-white leading-tight">{FASE_LABELS[fase]}</p>
          {subtitle && <p className="font-mono text-sm text-white/80 truncate">{subtitle}</p>}
        </div>
        {recording ? (
          <div className="flex items-center gap-2 shrink-0">
            <span className="w-3 h-3 rounded-full bg-wb-geldink animate-pulse" />
            <span className="font-mono text-base font-semibold text-white tabular-nums">
              {recordSeconds}s / {maxSeconds}s
            </span>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleClose}
            className="wb-focus shrink-0 min-h-tap px-5 rounded-wb bg-wb-mint text-wb-mintink border-wb border-wb-mintink font-wb-heading font-bold uppercase tracking-wide"
          >
            {count > 0 ? `Klaar (${count})` : 'Klaar'}
          </button>
        )}
      </div>

      {/* Bottom-overlay: thumbnails + bediening */}
      <div
        className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}
      >
        {/* Thumbnails van deze sessie */}
        {thumbs.length > 0 && (
          <div className="flex gap-2 px-4 pb-2">
            {thumbs.map((url) => (
              <img
                key={url}
                src={url}
                alt=""
                className="w-12 h-12 rounded-wb object-cover border-wb border-white"
              />
            ))}
          </div>
        )}

        {/* Opslag weigerde de laatste capture: dit moet de wasser stoppen, niet
            informeren. Doorklikken levert kleden op die niemand vastlegde. */}
        {opslagFout && (
          <div className="mx-4 mb-2 rounded-r-wb border-l-[6px] border-wb-geldink bg-wb-geld text-wb-geldink px-3 py-2">
            <p className="font-wb-heading font-bold text-sm uppercase tracking-wide">Foto niet opgeslagen</p>
            <p className="text-sm mt-1 leading-snug">{opslagFout}</p>
            <button
              type="button"
              onClick={() => setOpslagFout(null)}
              className="wb-focus mt-2 min-h-tap text-sm font-semibold underline underline-offset-4"
            >
              Ik heb het gelezen, opnieuw proberen
            </button>
          </div>
        )}

        {/* Social-filmpje (22-10): telefooncamera, 30 tot 60 s, staand en dichtbij */}
        {socialFilmpjeMogelijk && !recording && (
          <div className="px-4 pb-2">
            <input
              ref={socialInputRef}
              type="file"
              accept="video/*"
              capture="environment"
              className="hidden"
              onChange={(e) => void onSocialFilmpje(e)}
            />
            <button
              type="button"
              onClick={() => socialInputRef.current?.click()}
              disabled={opslagFout !== null}
              className="wb-focus w-full min-h-tap rounded-wb border-wb border-wb-mintink bg-wb-mint text-wb-mintink font-wb-heading font-bold text-sm uppercase tracking-wide disabled:opacity-30"
            >
              Social-filmpje
            </button>
            <p className="mt-1 text-center font-mono text-[11px] text-white/80">
              telefoon rechtop, dichtbij het kleed, 30 tot 60 seconden één handeling
            </p>
          </div>
        )}

        {/* Bediening: teller | sluiter | video */}
        <div className="flex items-center justify-between px-8 pt-1">
          <div className="w-16 text-white text-center">
            {count > 0 && (
              <span className="inline-flex items-center justify-center min-w-[40px] h-10 px-2 rounded-wb bg-white/25 font-mono text-lg font-semibold backdrop-blur-sm tabular-nums">
                {count}
              </span>
            )}
          </div>

          {/* Sluiter 84 px: groter dan taplg, het is de ene knop met handschoenen aan */}
          <button
            type="button"
            onClick={maakFoto}
            disabled={!ready || recording || opslagFout !== null}
            aria-label="Foto maken"
            className="wb-focus w-[84px] h-[84px] rounded-full border-4 border-white bg-white/25 active:bg-white/70 disabled:opacity-30 transition-colors backdrop-blur-sm"
          />

          <button
            type="button"
            onClick={recording ? stopVideo : startVideo}
            disabled={!ready || opslagFout !== null}
            aria-label={recording ? 'Video stoppen' : 'Video opnemen'}
            className={clsx(
              'wb-focus w-16 min-h-tap rounded-wb border-wb font-wb-heading font-bold text-xs uppercase tracking-wide transition-colors backdrop-blur-sm',
              recording ? 'border-wb-geldink bg-wb-geldink text-white' : 'border-white bg-black/20 text-white disabled:opacity-30'
            )}
          >
            {recording ? 'Stop' : 'Video'}
          </button>
        </div>
      </div>
    </div>
  );
}
